import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  TextField,
  Typography,
  IconButton,
  FormHelperText,
  Alert
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LOAD_PENDING_PAYMENT_INCOME_CFDI } from 'src/store/constants/CFDI';
import { loadPendingPaymentIncomeCFDIs } from 'src/store/actions/cfdiActions';
import Preloader from 'src/components/Preloader';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { loadClientContracts } from 'src/store/actions/clientActions';
import ContractStatus from 'src/utils/constants/contracts';
import { formatAmountWithDecimals } from 'src/utils/formatAmout';
import {
  getCancelWarningKey,
  invoiceTypeLabels
} from 'src/utils/constants/cfdi';
import MoneyMask from 'src/components/MoneyMask/MoneyMask';
import Autocomplete from 'src/components/Autocomplete/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import momentLocalTimezone from 'src/utils/momentLocalTimezone';
import DropdownMenu from 'src/components/DropdownMenu';
import { PaymentFormList } from 'src/utils/constants/transactions';
import makeInitialState from './initialState';
import validationSchema from './schema';

const StepTwoReceipts = ({
  data,
  handleSubmit,
  handleBack,
  loading,
  resetRef,
  isOlderThan72Hours,
  isAmountMoreThan5000
}) => {
  const { t } = useTranslation();
  const initialValues = makeInitialState(data);
  const dispatch = useDispatch();
  const [cfdiList, setCfdiList] = useState([]);
  const [cfdisCount, setCfdisCount] = useState(data.relatedDocuments.length);
  const isLoadingCfdis = useSelector(
    isLoadingSelector([LOAD_PENDING_PAYMENT_INCOME_CFDI.action])
  );

  const mapCfdisToRelatedDocuments = useCallback(relatedDocuments => {
    return relatedDocuments.map(relatedDocument => ({
      invoiceId: relatedDocument.id,
      amount: relatedDocument.amountIvaIncluded
    }));
  }, []);

  const onNext = useCallback(
    async values => {
      const requestData = {
        ...values,
        relatedDocuments: mapCfdisToRelatedDocuments(values.relatedDocuments),
        amount: values.totalAmount
      };

      delete requestData.cfdis;

      handleSubmit(requestData);
    },
    [handleSubmit, mapCfdisToRelatedDocuments]
  );

  useEffect(() => {
    if (data.clientId) {
      dispatch(
        loadClientContracts(
          { clientId: data.clientId, status: ContractStatus.IN_PROGRESS },
          false
        )
      );
    }
    if (data.contractId) {
      dispatch(loadPendingPaymentIncomeCFDIs(data.contractId, setCfdiList));
    }
    return () => {
      setCfdiList([]);
    };
  }, [
    dispatch,
    data.clientId,
    data.invoiceType,
    data.contractId,
    data.pendingPayments
  ]);
  const warningKeyForCancelDependingOnApproveOfClient = getCancelWarningKey(
    isOlderThan72Hours,
    isAmountMoreThan5000
  );

  return (
    <>
      {loading ? (
        <Preloader />
      ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize={false}
          validationSchema={validationSchema}
          onSubmit={onNext}
        >
          {({
            values,
            handleChange,
            errors,
            touched,
            handleBlur,
            setFieldValue,
            handleSubmit: formikHandleSubmit,
            resetForm
          }) => {
            resetRef.current = () => resetForm();

            const incrementInputCount = () => {
              if (cfdisCount < cfdiList.length) {
                setFieldValue('relatedDocuments', [...values.relatedDocuments, null], true);
              }
              setCfdisCount(preValue => preValue + 1);
            };

            const createHandleRemoveCfdi = index => () => {
              const updatedCfdis = values.relatedDocuments.filter(
                (_, i) => i !== index
              );
              setFieldValue('relatedDocuments', updatedCfdis, true);
              setCfdisCount(preValue => preValue - 1);
            };

            return (
              <form onSubmit={formikHandleSubmit}>
                <Box display="flex" flexDirection="column">
                  {warningKeyForCancelDependingOnApproveOfClient && (
                    <Alert severity="warning" sx={{ marginBottom: '1rem' }}>
                      {t(warningKeyForCancelDependingOnApproveOfClient)}
                    </Alert>
                  )}
                  <FormControl fullWidth>
                    <TextField
                      id="folio"
                      error={Boolean(touched.folio && errors.folio)}
                      fullWidth
                      helperText={touched.folio && t(errors.folio)}
                      label={t('CFDI_FORM.FOLIO')}
                      margin="normal"
                      name="folio"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.folio}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="invoiceType"
                      error={Boolean(touched.invoiceType && errors.invoiceType)}
                      fullWidth
                      helperText={touched.invoiceType && t(errors.invoiceType)}
                      label={t('CFDI_FORM.INVOICE_TYPE')}
                      margin="normal"
                      name="invoiceType"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={invoiceTypeLabels[values.invoiceType]}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="contractId"
                      error={Boolean(touched.contractId && errors.contractId)}
                      fullWidth
                      helperText={touched.contractId && t(errors.contractId)}
                      label={t('CFDI_FORM.CONTRACT_ID')}
                      margin="normal"
                      name="contractId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.contractId}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="clientId"
                      error={Boolean(touched.clientId && errors.clientId)}
                      fullWidth
                      helperText={touched.clientId && t(errors.clientId)}
                      label={t('CFDI_FORM.CLIENT_ID')}
                      margin="normal"
                      name="clientId"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.clientId}
                      variant="outlined"
                    />
                  </FormControl>
                  <FormControl fullWidth>
                    <TextField
                      id="clientName"
                      error={Boolean(touched.clientName && errors.clientName)}
                      fullWidth
                      helperText={touched.clientName && t(errors.clientName)}
                      label={t('CFDI_FORM.CLIENT_NAME')}
                      margin="normal"
                      name="clientName"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled
                      value={values.clientName}
                      variant="outlined"
                    />
                  </FormControl>

                  <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                    {t('COMMON.PAYMENT_DETAILS')}
                  </Typography>

                  <FormControl fullWidth>
                    <Box
                      sx={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        padding: '8px',
                        marginBottom: '16px'
                      }}
                    >
                      {Array(cfdisCount)
                        .fill('')
                        .map((input, index) => (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexDirection: 'column',
                              marginTop: '16px',
                              padding: '8px',
                              borderBottom:
                                index < cfdisCount - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none'
                            }}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`box-${index}`}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}
                            >
                              <Autocomplete
                                // eslint-disable-next-line react/no-array-index-key
                                key={`cfdi-${index}$`}
                                options={cfdiList.filter(
                                  cfdi =>
                                    !values.relatedDocuments.some(
                                      selectedCfdi => {
                                        if (selectedCfdi) {
                                          return selectedCfdi.id === cfdi.id;
                                        }
                                        return false;
                                      }
                                    )
                                )}
                                id="relatedDocuments"
                                label={t('COMMON.CFDI')}
                                value={values.relatedDocuments[index] ?? null}
                                loading={isLoadingCfdis}
                                disabled={isLoadingCfdis || !values.contractId}
                                loadingText={t('COMMON.LOADING')}
                                getOptionLabel={option =>
                                  option
                                    ? `${t('COMMON.SERIE')} ${
                                        option.series
                                      } - ${
                                        option.folio
                                      } - $${formatAmountWithDecimals(
                                        option?.amountIvaIncluded
                                      )}`
                                    : ''
                                }
                                onChange={(_e, value) => {
                                  const updatedCfdis = [
                                    ...values.relatedDocuments
                                  ];
                                  if (value) {
                                    updatedCfdis[index] = value
                                      ? {
                                          ...value,
                                          amountToPay: value?.amountIvaIncluded
                                        }
                                      : null;
                                  }
                                  setFieldValue(
                                    'relatedDocuments',
                                    updatedCfdis
                                  );
                                }}
                                error={Boolean(
                                  touched.relatedDocuments &&
                                    errors.relatedDocuments &&
                                    errors.relatedDocuments[index]
                                )}
                                sx={{
                                  width: '60%'
                                }}
                              />

                              <TextField
                                sx={{
                                  width: '32%'
                                }}
                                InputProps={{
                                  autoComplete: 'off',
                                  inputComponent: MoneyMask,
                                }}
        InputLabelProps={{ shrink: true }}
                                // eslint-disable-next-line react/no-array-index-key
                                key={`amount-${index}`}
                                error={Boolean(
                                  touched.relatedDocuments &&
                                    errors.relatedDocuments &&
                                    errors.relatedDocuments[index]
                                )}
                                disabled={
                                  !values.relatedDocuments[index] ||
                                  isLoadingCfdis
                                }
                                label={t('COMMON.AMOUNT_WITH_IVA')}
                                onBlur={handleBlur}
                                name={`relatedDocuments[${index}].amountIvaIncluded`}
                                onChange={e => {
                                  const newValues = [
                                    ...values.relatedDocuments
                                  ];
                                  if (
                                    values.relatedDocuments[index]
                                      ?.amountIvaIncluded ||
                                    values.relatedDocuments[index]
                                      ?.amountIvaIncluded === 0
                                  ) {
                                    if (
                                      e.target.value ||
                                      e.target.value === 0
                                    ) {
                                      newValues[index] = {
                                        ...newValues[index],
                                        amountIvaIncluded: e.target.value
                                      };
                                    }
                                  }

                                  setFieldValue('relatedDocuments', newValues);
                                }}
                                value={
                                  values.relatedDocuments[index]
                                    ?.amountIvaIncluded ?? ''
                                }
                                variant="outlined"
                              />
                              <IconButton
                                sx={{
                                  width: '20px',
                                  height: '20px',
                                  paddingLeft: '3px'
                                }}
                                aria-label="delete"
                                // eslint-disable-next-line react/no-array-index-key
                                key={`delete-${index}`}
                                disabled={cfdisCount === 1}
                                onClick={createHandleRemoveCfdi(index)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>

                            <FormHelperText error>
                              {touched.relatedDocuments &&
                              errors.relatedDocuments &&
                              errors.relatedDocuments[index]
                                ? t(errors.relatedDocuments[index])
                                : ' '}
                            </FormHelperText>
                          </Box>
                        ))}
                    </Box>
                  </FormControl>

                  <Box
                    key="addButton"
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      type="button"
                      key="addButton"
                      color="primary"
                      variant="text"
                      disabled={isLoadingCfdis || cfdisCount > cfdiList.length}
                      onClick={incrementInputCount}
                      startIcon={<AddIcon />}
                    >
                      {t('COMMON.ADD')}
                    </Button>
                  </Box>

                  <TextField
                    fullWidth
                    margin="normal"
                    name="totalAmount"
                    id="totalAmount"
                    disabled
                    label={t('COMMON.TOTAL_AMOUNT')}
                    InputProps={{
                      readOnly: true,
                      inputComponent: MoneyMask,
                    }}
        InputLabelProps={{ shrink: true }}
                    value={formatAmountWithDecimals(
                      values.relatedDocuments.reduce((acc, cfdi) => {
                        if (cfdi?.amountIvaIncluded) {
                          return acc + cfdi.amountIvaIncluded;
                        }
                        return acc;
                      }, 0)
                    )}
                  />

                  <FormControl fullWidth>
                    <DropdownMenu
                      list={PaymentFormList}
                      value={values.paymentForm}
                      name="paymentForm"
                      setValue={value => setFieldValue('paymentForm', value)}
                      label={t('TRANSACTION.PAYMENT_FORM')}
                      error={Boolean(touched.paymentForm && errors.paymentForm)}
                      errorText={touched.paymentForm && t(errors.paymentForm)}
                      disabled={loading || isLoadingCfdis}
                    />
                  </FormControl>

                  <FormControl fullWidth>
                    <DatePicker
                      name="date"
                      label={t('TRANSACTION.DATE')}
                      value={values.date || null}
                      onChange={newValue => {
                        setFieldValue('date', momentLocalTimezone(newValue));
                      }}
                      disabled={loading || isLoadingCfdis}
                      slotProps={{
                        textField: {
                          sx: { width: '100%', margin: '16px 0' },
                          error: Boolean(touched.date && errors.date),
                          helperText: touched.date && t(errors.date),
                          FormHelperTextProps: { sx: { color: 'tomato' } },
                          InputProps: { autoComplete: 'off' }
                        }
                      }}
                    />
                  </FormControl>

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    mt={3}
                    sx={{ width: '100%' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      sx={{ minWidth: '45%' }}
                      disabled={loading || isLoadingCfdis}
                    >
                      {t('COMMON.BACK')}
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading || isLoadingCfdis}
                      sx={{ minWidth: '45%' }}
                    >
                      {loading ? (
                        <CircularProgress size={10} />
                      ) : (
                        t('COMMON.ACCEPT')
                      )}
                    </Button>
                  </Box>
                </Box>
              </form>
            );
          }}
        </Formik>
      )}
    </>
  );
};

StepTwoReceipts.propTypes = {
  data: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleBack: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  resetRef: PropTypes.object.isRequired,
  isOlderThan72Hours: PropTypes.bool.isRequired,
  isAmountMoreThan5000: PropTypes.bool.isRequired
};
export default StepTwoReceipts;
